/**
 * Session Engine v3 — canonical session store.
 *
 * Wired to Supabase. Each SessionEvent corresponds to a real
 * prioritization_sessions row. V3 step configuration, custom criteria, values,
 * and scenarios are persisted in mode_config.v3Config (jsonb). Participants and
 * item IDs use the canonical session_participants and item_ids columns.
 */

import { normalizeToolConfig } from '../../lib/toolSettings';
import { normalizeSessionToolId } from '../../lib/sessionToolCatalog';
import { create } from 'zustand';
import {
  applyCutLineToPoolItems,
  cannotCoexistConflicts,
  computeRanking,
  deriveExclusivityGroups,
  evaluateExclusivity,
} from './engineLogic';
import { getJob } from './sessionJobs';
import type { SituationVector } from '../../types/prioritization/situationVector';
import { deriveInheritedRoster } from './inheritedRoster';
import { createSessionWithFeedback } from '../../lib/sessions/createSessionFlow';
import { useItemRelationshipsStore } from '../../stores/prioritization/itemRelationshipsStore';
import {
  STEP_ORDER,
  STEP_META,
  type ContainerMode,
  type ContextMode,
  type CriterionDef,
  type DriverWeightLink,
  type SessionEvent,
  type EngineItem,
  type ItemInputSource,
  type ParticipantRef,
  type ParticipantRole,
  type RunMode,
  type RunStatus,
  type Scenario,
  type StepConfig,
  type StepId,
  type StepMode,
} from './types';

export interface AddItemsConflict {
  candidate: { id?: string; title: string };
  conflictingItemIds: string[];
}

export interface AddItemsResult {
  added: EngineItem[];
  conflicts: AddItemsConflict[];
}

// ─── helpers ────────────────────────────────────────────────────────────────

function uid(prefix: string): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * `createEvent`/`startStepRun` create their session via `createSessionWithFeedback`
 * but have no local error UI of their own — the optimistic `event` state just
 * quietly reverts `persisting` to false on failure. Dispatching `store-error`
 * routes the real reason through the app's existing global handler
 * (`useStoreErrorHandler`, mounted in `PrioritizationLayout`) instead of
 * leaving the user staring at a create button that silently did nothing.
 */
function reportSessionCreateFailure(action: string, message: string): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('store-error', {
    detail: { store: 'SessionEngineV3', action, message },
  }));
}

function normalizeStepsForContainer(
  steps: Record<StepId, StepConfig>,
  containerMode: ContainerMode,
): Record<StepId, StepConfig> {
  if (containerMode !== 'single') return steps;
  return Object.fromEntries(
    STEP_ORDER.map((step) => [step, { ...steps[step], runMode: 'live' as const }]),
  ) as Record<StepId, StepConfig>;
}

function buildSteps(jobId?: string): Record<StepId, StepConfig> {
  const job = getJob(jobId);
  const steps = {} as Record<StepId, StepConfig>;
  for (const step of STEP_ORDER) {
    const spec = job?.steps.find((s) => s.step === step);
    steps[step] = {
      step,
      required: spec?.required ?? (step === 'S5'),
      mode: spec?.defaultMode ?? (step === 'S5' ? 'collaborative' : 'declared'),
      toolId: spec?.recommendedToolIds?.[0],
      goal: undefined,
      runMode: 'live',
      runStatus: 'not_started',
    };
  }
  return steps;
}

/**
 * Which steps are IN SCOPE for a goal — the ones offered in the rail so the
 * creator can fill them in if they want to.
 *
 * This is the canonical convergence model (docs/1-canon/01-terminology-and-glossary.md
 * — S1–S5 Dependency Matrix) read as an offer, not a gate. Being in scope does
 * not make a step mandatory: what a goal genuinely cannot proceed without is
 * `stepPrerequisites` in engineLogic.ts, and `isStepMandatory` is what labels a
 * rail entry "required" rather than "optional". A creator may skip every
 * optional step (owner ruling 2026-09-22).
 */
export const GOAL_STEP_SCOPE: Record<StepId, StepId[]> = {
  S1: ['S3', 'S4'],        // Criteria + values sharpen who qualifies — optional
  S2: ['S1', 'S3', 'S4'], // Participants, criteria, values inform selection — optional
  S3: ['S1'],              // Criteria are evaluation dimensions — participants optional
  S4: ['S1', 'S2', 'S3'], // Values need items + criteria; participants optional
  S5: ['S1', 'S2', 'S3', 'S4'], // Ranking needs items; the rest depend on the tool
};

/**
 * Build steps from a primary goal step.
 * The goal step is collaborative; the in-scope steps are declared by default
 * and the facilitator can switch any to collaborative (subject to the
 * participants rule). `required` here means "in scope for this event", which is
 * what puts it in the rail — mandatory-ness is computed, not stored.
 */
export function buildStepsFromGoal(primaryStep: StepId): Record<StepId, StepConfig> {
  const requiredSteps = new Set<StepId>([primaryStep, ...GOAL_STEP_SCOPE[primaryStep]]);
  const steps = {} as Record<StepId, StepConfig>;
  for (const step of STEP_ORDER) {
    const isGoal = step === primaryStep;
    const isRequired = requiredSteps.has(step);
    steps[step] = {
      step,
      required: isRequired,
      mode: isGoal ? 'collaborative' : 'declared',
      toolId: undefined,
      goal: undefined,
      runMode: 'live',
      runStatus: 'not_started',
    };
  }
  return steps;
}

/** Map a primary goal step to the canonical session intent. */
function intentFromGoalStep(step?: StepId): 'select_attributes' | 'converge_values' | 'select_item_pool' | 'rank_priorities' | 'calibration' | 'review' {
  switch (step) {
    case 'S2': return 'select_item_pool';
    case 'S3': return 'select_attributes';
    case 'S4': return 'converge_values';
    case 'S5': return 'rank_priorities';
    // S1 (participation) has no dedicated canonical intent — closest is select_item_pool
    default:   return 'rank_priorities';
  }
}

/** Legacy job-id fallback — kept for any remaining job-ID references. */
function resolveIntent(jobId?: string, primaryStep?: StepId): 'select_attributes' | 'converge_values' | 'select_item_pool' | 'rank_priorities' | 'calibration' | 'review' {
  if (primaryStep) return intentFromGoalStep(primaryStep);
  const map: Record<string, 'select_attributes' | 'converge_values' | 'select_item_pool' | 'rank_priorities' | 'calibration' | 'review'> = {
    select_item_pool: 'select_item_pool',
    converge_values: 'converge_values',
    calibration: 'calibration',
  };
  return jobId && jobId in map ? map[jobId] : 'rank_priorities';
}

// ─── V3 config sync ─────────────────────────────────────────────────────────

/** Fire-and-forget: persist V3 step/criteria/values state to mode_config.v3Config.
 *  Also syncs selectedToolId to the session row when the primary goal step has a tool. */
/**
 * Persist the parent→child link, and keep trying until it lands.
 *
 * Event→Session parentage has no column of its own: it lives only in this
 * childSessions map inside mode_config.v3Config. Everything else in v3Config is
 * a convenience that a reload can rebuild — this is the ONLY record that a given
 * sitting belongs to a given Event. It was persisted the same way as the rest,
 * by syncV3Config's fire-and-forget write with a console.warn on failure, so a
 * dropped write orphaned the child permanently and silently: it then reappears
 * as a top-level row and is indistinguishable from an unrelated session on the
 * same track.
 *
 * Awaited, with retries. The durable fix is a parent_session_id column on
 * prioritization_sessions; until that exists this write must not be best-effort.
 */
async function persistChildLink(
  parentSessionId: string,
  childSessions: Partial<Record<StepId, string>>,
  /** The child just created, written to its own parent_session_id column. */
  child?: { id: string; step: StepId },
): Promise<boolean> {
  // Migration 694 gave parentage a real column. Write it on the CHILD row, so
  // the edge points both ways and the database enforces it: one parent per
  // sitting, one sitting per step, cascade on delete. The blob write below stays
  // for readers that have not moved yet.
  if (child) {
    try {
      const { getSafeSupabaseClient, isSupabaseAvailable } = await import('../../lib/supabaseSync');
      if (isSupabaseAvailable()) {
        const supabase = getSafeSupabaseClient();
        if (supabase) {
          const { error } = await supabase
            .from('prioritization_sessions')
            .update({ parent_session_id: parentSessionId, parent_step_id: child.step })
            .eq('id', child.id);
          if (error) console.error('[SessionEngineV3] parent_session_id write failed:', error.message);
        }
      }
    } catch (e) {
      console.error('[SessionEngineV3] parent_session_id write threw:', e);
    }
  }

  const { useSessionStore } = await import('../../stores/prioritization/sessionStore');
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      await useSessionStore.getState().updateModeConfig(parentSessionId, {
        v3Config: { childSessions },
      } as unknown as import('../../types/prioritization/session').SessionModeConfig);
      return true;
    } catch (e) {
      if (attempt === 2) {
        console.error('[SessionEngineV3] Could not record child-session parentage:', e);
        return false;
      }
      await new Promise((r) => setTimeout(r, 200 * (attempt + 1)));
    }
  }
  return false;
}

function syncV3Config(sessionId: string, event: SessionEvent): void {
  void (async () => {
    try {
      const { useSessionStore } = await import('../../stores/prioritization/sessionStore');
      const { steps, criteria, values, scenarios, activeScenarioId, containerMode, contextMode, jobId, primaryGoalStep, poolState, stepHistory, participants, items, childSessions, cycleName, trackName, driverWeightLink } = event;
      await useSessionStore.getState().updateModeConfig(sessionId, {
        v3Config: {
          steps, criteria, values, scenarios, activeScenarioId,
          containerMode, contextMode, jobId, primaryGoalStep, poolState, stepHistory, childSessions, cycleName, trackName,
          driverWeightLink,
          // Persist participants and items so LiveSessionPage can resolve them
          // when auto-creating the tool session without access to the V3 store.
          participants: participants.map(p => ({ userId: p.userId, name: p.name })),
          items: items.map(i => ({ id: i.id, title: i.title, source: i.source })),
        },
      } as unknown as import('../../types/prioritization/session').SessionModeConfig);

      // Mirror the primary goal step's tool onto the canonical session row so
      // the rest of the platform (reporting, live session, API) can find it.
      const goalStepTool = primaryGoalStep ? steps[primaryGoalStep]?.toolId : undefined;
      if (goalStepTool) {
        await useSessionStore.getState().updateSession(sessionId, { selectedToolId: goalStepTool } as never);
      }

      // And the criteria, for the same reason. criteriaConfig used to be
      // written only when a ranking was computed, so a sitting that declared
      // Impact and Effort and then ran any other tool left criteriaIds empty —
      // and the live scoring tool, finding none, fell back to its own three
      // and asked the room to score dimensions the session never chose.
      // Written here, at the end of the sync, because updateSession rewrites
      // the whole row from the local copy: an earlier write of this column is
      // simply undone by the next write of any other one.
      if (criteria.length > 0) {
        const store = useSessionStore.getState();
        const criteriaConfig = {
          ...(store.getSession(sessionId)?.criteriaConfig ?? { reconciliationRequiredSet: [] }),
          criteriaIds: criteria.map((c) => c.id),
          weights: Object.fromEntries(criteria.map((c) => [c.id, c.weight])),
        };
        const { getSafeSupabaseClient } = await import('../../lib/supabaseSync');
        const supabase = getSafeSupabaseClient();
        if (supabase) {
          const { error: criteriaWriteError } = await supabase
            .from('prioritization_sessions')
            .update({ criteria_config: JSON.parse(JSON.stringify(criteriaConfig)) })
            .eq('id', sessionId);
          if (criteriaWriteError) {
            console.warn('[SessionEngineV3] could not persist criteria_config:', criteriaWriteError);
          }
        }
        useSessionStore.setState((st) => ({
          sessions: st.sessions.map((x) => (x.id === sessionId ? { ...x, criteriaConfig } : x)),
        }));
      }
    } catch (e) {
      console.warn('[SessionEngineV3] syncV3Config failed:', e);
    }
  })();
}

// ─── store interface ─────────────────────────────────────────────────────────

interface EngineState {
  event: SessionEvent | null;

  createEvent: (opts: {
    title: string;
    jobId?: string;
    primaryStep?: StepId;
    containerMode: ContainerMode;
    contextMode: ContextMode;
    cycleId?: string;
    cycleName?: string;
    trackId?: string;
    trackName?: string;
    /** When created from Catchball — appends this session onto the loop. */
    loopId?: string;
    catchballBallId?: string;
    /** The four setup questions, when the facilitator answered any of them. */
    situationVector?: SituationVector | null;
    /** A method chosen before the Designer opened — becomes S5's tool and the session's selected tool. */
    toolId?: string;
    /** The prioritization context this session belongs to (prioritization_sessions.context_id). */
    contextId?: string;
  }) => Promise<void>;
  loadEvent: (sessionId: string) => Promise<void>;
  resetEvent: () => void;

  setContainerMode: (mode: ContainerMode) => void;
  setPoolState: (state: SessionEvent['poolState']) => void;

  setStepMode: (step: StepId, mode: StepMode) => void;
  setStepTool: (step: StepId, toolId: string | undefined) => void;
  setStepGoal: (step: StepId, goal: string) => void;
  setStepRunMode: (step: StepId, runMode: RunMode) => void;
  setStepRunStatus: (step: StepId, runStatus: RunStatus) => void;
  startStepRun: (step: StepId) => Promise<string | null>;
  /** Unlock a completed step for revision — resets runStatus and records history. */
  unlockStep: (step: StepId, reason?: string) => void;

  /** Add a participant. Pass userId when the user was selected from the workspace roster. */
  addParticipant: (name: string, role: ParticipantRole, userId?: string) => void;
  /** Change a participant's role in place — never remove-then-add (F7). */
  setParticipantRole: (id: string, role: ParticipantRole) => void;
  removeParticipant: (id: string) => void;

  addItems: (rows: { title: string; source: ItemInputSource; id?: string }[]) => AddItemsResult;
  removeItem: (id: string) => void;

  setCriteria: (criteria: CriterionDef[]) => void;
  /**
   * Bind criteria weights to the cycle strategy's driver weights, or release
   * them. Releasing keeps the current weights — it stops them following, it
   * does not revert them.
   */
  setDriverWeightLink: (link: DriverWeightLink | undefined) => void;
  /** Merge already-accepted values into S4 without clobbering entered ones. */
  hydrateValues: (incoming: Record<string, Record<string, number>>) => void;
  setValue: (itemId: string, criterionId: string, value: number) => void;

  runRanking: () => void;
  setManualRanking: (ranking: string[]) => void;

  addScenario: (name: string, cutLine: number, selectedItemIds?: string[], notes?: string) => void;
  updateScenario: (id: string, patch: Partial<Pick<Scenario, 'name' | 'cutLine' | 'selectedItemIds' | 'notes'>>) => void;
  removeScenario: (id: string) => void;
  setActiveScenario: (id: string | undefined) => void;

  /**
   * S2 accept: rewrite the event item pool to only items above the cut line.
   * Saves a scenario snapshot, prunes values, locks the pool, marks S2 run complete
   * when the step is collaborative.
   */
  acceptCutLineToPool: (rankedItemIds: string[], cutLine: number, notes?: string) => {
    ok: boolean;
    error?: string;
    keptCount?: number;
    droppedCount?: number;
  };
}

// ─── patch helper ────────────────────────────────────────────────────────────

function patch(
  set: (fn: (s: EngineState) => Partial<EngineState>) => void,
  fn: (e: SessionEvent) => SessionEvent,
  sync = true,
) {
  set((s) => {
    if (!s.event) return {};
    const next = fn(s.event);
    if (sync && next.id && !next.persisting) syncV3Config(next.id, next);
    return { event: next };
  });
}

// ─── store ───────────────────────────────────────────────────────────────────

export const useSessionEngineStore = create<EngineState>((set, get) => ({
  event: null,

  // ── Create (wired to Supabase) ─────────────────────────────────────────────
  createEvent: async ({
    title,
    jobId,
    primaryStep,
    containerMode,
    contextMode,
    cycleId,
    cycleName,
    trackId,
    trackName,
    loopId,
    catchballBallId,
    situationVector,
    toolId: preselectedToolId,
    contextId,
  }) => {
    const job = getJob(jobId);
    // Goal-based step config takes priority over job templates
    const builtSteps = primaryStep ? buildStepsFromGoal(primaryStep) : buildSteps(jobId);
    // A tool picked before the Designer opened (a recommended tool on the
    // Sessions list) is the ranking step's tool — not lost on the way in.
    const selectedToolId = normalizeSessionToolId(preselectedToolId);
    const steps = selectedToolId
      ? { ...builtSteps, S5: { ...builtSteps.S5, toolId: selectedToolId } }
      : builtSteps;

    // Optimistic local state immediately (no blocking)
    const optimisticEvent: SessionEvent = {
      id: uid('evt'),
      title,
      jobId,
      primaryGoalStep: primaryStep,
      containerMode: containerMode ?? job?.containerDefault ?? 'single',
      contextMode,
      cycleId,
      cycleName,
      trackId,
      trackName,
      situationVector: situationVector ?? null,
      poolState: 'open',
      steps,
      participants: [],
      items: [],
      criteria: [],
      values: {},
      ranking: [],
      scenarios: [],
      childSessions: {},
      stepHistory: [],
      activeScenarioId: undefined,
      persisting: true,
    };
    set({ event: optimisticEvent });

    try {
      const { useSessionStore } = await import('../../stores/prioritization/sessionStore');
      const { useUserStore } = await import('../../stores/prioritization/userStore');

      const userId = useUserStore.getState().userId;

      const createResult = await createSessionWithFeedback({
        name: title,
        facilitatorId: userId,
        createdBy: userId,
        // Use canonical intent from primaryGoalStep; falls back to jobId mapping
        intent: resolveIntent(jobId, primaryStep),
        sessionContextMode: contextMode,
        cycleId: cycleId ?? undefined,
        trackId: trackId ?? null,
        catchballBallId: catchballBallId ?? null,
        situationVector: situationVector ?? null,
        ...(selectedToolId ? { selectedToolId } : {}),
        ...(contextId ? { contextId } : {}),
        originModule: 'prioritization',
        // No run mode of its own: on a cycle or track the event starts from the
        // chain's (S-40, migration 1039); each step still sets how it runs.
      });
      const session = createResult.session;

      if (session) {
        // Update optimistic ID to real session UUID
        set((s) => s.event ? { event: { ...s.event, id: session.id, persisting: false } } : {});
        try { localStorage.setItem('v3ActiveEventId', session.id); } catch { /* noop */ }

        // Catchball entry: append this sitting onto the exchange loop so it
        // appears under "Sessions on this track" with results when complete.
        if (loopId) {
          try {
            const { linkSessionToCatchballLoop } = await import('../../lib/catchballSessionLink');
            await linkSessionToCatchballLoop(loopId, session.id);
          } catch (linkErr) {
            console.warn('[SessionEngineV3] Created session but failed to link to Catchball loop:', linkErr);
          }
        }

        // ── Track inheritance (cycle_linked only) ─────────────────────────────
        // When the Event is tied to a Track, inherit the effective
        // participant set and the track's item pool automatically. This mirrors
        // what WorkspaceSessionWizard does: it passes itemIds + adds participants
        // after create. V3 does the same here so the platform's inheritance
        // pipeline is respected instead of rebuilt from scratch.
        if (contextMode === 'cycle_linked' && trackId && userId) {
          try {
            const { useSessionParticipantsStore } = await import('../../stores/prioritization/sessionParticipantsStore');
            const { useItemsStore: _useItemsStore } = await import('../../stores/prioritization/itemsStore');
            const { getSafeSupabaseClient } = await import('../../lib/supabaseSync');
            const supabase = getSafeSupabaseClient();

            if (supabase) {
              // Effective participation is cycle → track, users and groups —
              // and effectiveParticipantResolver already owns that union.
              // Reading track_participants alone dropped a cycle-level
              // committee out of its own S1: a committee seated at the cycle
              // with no track rows opened every session with a roster of one.
              const { resolveEffectiveParticipantsForTrack } = await import('../../lib/effectiveParticipantResolver');
              const { mergeInheritedRoles } = await import('./inheritedRoster');
              const { getMemberships } = await import('../../lib/groupsService');

              const emptyRows = { data: [] as { user_id?: string; group_id?: string; role?: string | null }[] };
              const [trackRes, cycleRes, trackGroupRes, cycleGroupRes] = await Promise.all([
                supabase.from('track_participants').select('user_id, role').eq('track_id', trackId).eq('status', 'active'),
                cycleId
                  ? supabase.from('cycle_participants').select('user_id, role').eq('cycle_id', cycleId).eq('status', 'active')
                  : Promise.resolve(emptyRows),
                supabase.from('track_participant_groups').select('group_id').eq('track_id', trackId).eq('status', 'active'),
                cycleId
                  ? supabase.from('cycle_participant_groups').select('group_id').eq('cycle_id', cycleId).eq('status', 'active')
                  : Promise.resolve(emptyRows),
              ]);

              const cycleDirectRows = (cycleRes.data ?? []) as { user_id: string; role?: string | null }[];
              const trackDirectRows = (trackRes.data ?? []) as { user_id: string; role?: string | null }[];

              const effective = await resolveEffectiveParticipantsForTrack({
                cycleDirectUsers: cycleDirectRows.map((r) => ({ userId: r.user_id })),
                cycleParticipantGroups: ((cycleGroupRes.data ?? []) as { group_id: string }[]).map((r) => ({ groupId: r.group_id })),
                trackDirectUsers: trackDirectRows.map((r) => ({ userId: r.user_id })),
                trackParticipantGroups: ((trackGroupRes.data ?? []) as { group_id: string }[]).map((r) => ({ groupId: r.group_id })),
                getMemberUserIdsForGroup: async (groupId: string) => {
                  const memberships = await getMemberships({ groupId });
                  return [...new Set(memberships.map((m) => m.userId).filter(Boolean))];
                },
              });

              const trackParticipantRows = mergeInheritedRoles(
                effective.map((e) => e.userId),
                cycleDirectRows,
                trackDirectRows,
              );

              // Fetch workspace user names
              const participantUserIds = (trackParticipantRows ?? []).map((r: { user_id: string }) => r.user_id).filter(Boolean);
              const allIds = Array.from(new Set([...participantUserIds]));

              let nameMap: Record<string, string> = {};
              if (allIds.length > 0) {
                const { data: userRows } = await supabase.from('users').select('id, name, email').in('id', allIds);
                if (userRows) nameMap = Object.fromEntries(userRows.map((u: { id: string; name?: string; email: string }) => [u.id, u.name || u.email]));
              }

              // Add each track participant to session_participants + local event
              // state. F7: the initiator is included — createSession only
              // stamps facilitatorId on the session row, it writes no
              // session_participants row, so skipping them here dropped a
              // facilitator who was also a track member out of their own S1
              // roster. addParticipant is idempotent, so re-adding is safe.
              const addParticipant = useSessionParticipantsStore.getState().addParticipant;
              const newParticipants: ParticipantRef[] = [];

              for (const inherited of deriveInheritedRoster(
                (trackParticipantRows ?? []) as { user_id: string; role?: string | null }[],
                userId,
              )) {
                const pid = inherited.userId;
                const name = nameMap[pid] ?? (pid === userId ? 'You' : pid);
                const role = inherited.role as ParticipantRole;
                void addParticipant({ sessionId: session.id, userId: pid, role, addedBy: userId });
                newParticipants.push({ id: uid('p'), name, userId: pid, role });
              }

              // Pull items from the track's item pool if available
              const { data: poolRows } = await supabase
                .from('item_pools')
                .select('priority_ids')
                .eq('track_id', trackId)
                .limit(1)
                .maybeSingle();

              const inheritedItemIds: string[] = (poolRows as { priority_ids?: string[] } | null)?.priority_ids ?? [];
              let inheritedItems: EngineItem[] = [];

              if (inheritedItemIds.length > 0) {
                // Fetch item titles from the platform items store / DB
                const { data: platformItems } = await supabase
                  .from('items')
                  .select('id, title')
                  .in('id', inheritedItemIds);
                inheritedItems = (platformItems ?? []).map((i: { id: string; title: string }) => ({
                  id: i.id,
                  title: i.title,
                  source: 'platform' as const,
                }));
                // Write canonical item_ids to the session row
                void useSessionStore.getState().updateSession(session.id, { itemIds: inheritedItemIds } as never);
              }

              // Pull criteria from the track's attribute configs (Criteria & Scales tab)
              let inheritedCriteria: CriterionDef[] = [];
              try {
                const { data: criteriaRows } = await supabase
                  .from('cycle_attribute_configs')
                  .select('attribute_key, range_status, range_value, weight, collection_method, specific_person_id')
                  .eq('track_id', trackId)
                  .order('created_at', { ascending: true });

                if (criteriaRows && criteriaRows.length > 0) {
                  const { useAttributesStore } = await import('../../stores/prioritization/attributesStore');
                  const { criteriaFromTrackAttributeKeys } = await import('../../lib/sessionCriteriaFromTrack');
                  const { resolveValuesFrom } = await import('../../lib/valuesFrom');
                  const { useAttributeGovernanceStore } = await import('../../stores/prioritization/attributeGovernanceStore');
                  const allAttrs = useAttributesStore.getState().attributes;
                  const governance = useAttributeGovernanceStore.getState();
                  const rows = criteriaRows as {
                    attribute_key: string; weight: number | null;
                    collection_method: string | null; specific_person_id: string | null;
                  }[];
                  // Same derivation the phone wizard shows pre-create — one rule, one place.
                  // The track's weights when every criterion has one (migration 1047), else an even split.
                  // Where each criterion's values come from (1062): the track's row, else the
                  // cycle's override, else the workspace's attribute setting.
                  inheritedCriteria = criteriaFromTrackAttributeKeys(
                    rows.map((row) => row.attribute_key),
                    (key) => allAttrs.find((a) => a.key === key)?.label,
                    (key) => rows.find((row) => row.attribute_key === key)?.weight,
                    (key) => {
                      const row = rows.find((r) => r.attribute_key === key);
                      const resolved = resolveValuesFrom({
                        track: row ? { collectionMethod: row.collection_method, specificPersonId: row.specific_person_id } : null,
                        cycle: cycleId ? governance.getCycleOverride(cycleId, key) ?? null : null,
                        workspace: governance.getTemplateByKey(key) ?? null,
                      });
                      return resolved.from ? { from: resolved.from, personId: resolved.personId } : null;
                    },
                  );
                }
              } catch (criteriaErr) {
                console.warn('[SessionEngineV3] Criteria inheritance failed:', criteriaErr);
              }

              // Patch local event state with inherited participants, items, and criteria
              if (newParticipants.length > 0 || inheritedItems.length > 0 || inheritedCriteria.length > 0) {
                set((s) => {
                  if (!s.event) return {};
                  return {
                    event: {
                      ...s.event,
                      participants: newParticipants,
                      items: inheritedItems,
                      ...(inheritedCriteria.length > 0 ? { criteria: inheritedCriteria } : {}),
                    },
                  };
                });
              }
            }
          } catch (inheritErr) {
            // Inheritance is best-effort; don't fail the create
            console.warn('[SessionEngineV3] Track inheritance failed:', inheritErr);
          }
        }

        // Write final V3 config now that participants + items are resolved
        const freshEvent = get().event;
        if (freshEvent) syncV3Config(session.id, freshEvent);
      } else {
        reportSessionCreateFailure('createEvent', createResult.error ?? 'Session could not be created.');
        set((s) => s.event ? { event: { ...s.event, persisting: false } } : {});
      }
    } catch (err) {
      console.error('[SessionEngineV3] createEvent failed:', err);
      reportSessionCreateFailure('createEvent', err instanceof Error ? err.message : 'Session could not be created.');
      set((s) => s.event ? { event: { ...s.event, persisting: false } } : {});
    }
  },

  // ── Load (reload from Supabase by session ID) ──────────────────────────────
  loadEvent: async (sessionId: string) => {
    // Guard: must be a real UUID, not a route slug
    if (!sessionId || !/^[0-9a-f-]{36}$/i.test(sessionId)) {
      try { localStorage.removeItem('v3ActiveEventId'); } catch { /* noop */ }
      return;
    }
    try {
      const { useSessionStore } = await import('../../stores/prioritization/sessionStore');
      const { useItemsStore } = await import('../../stores/prioritization/itemsStore');
      const { useSessionParticipantsStore } = await import('../../stores/prioritization/sessionParticipantsStore');

      let session = useSessionStore.getState().getSession(sessionId);
      if (!session) {
        await useSessionStore.getState().fetchSessionById(sessionId);
        session = useSessionStore.getState().getSession(sessionId);
      }
      if (!session) return;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const v3 = (session.modeConfig as any)?.v3Config ?? {};

      const loadedContainerMode = (v3.containerMode as ContainerMode) ?? 'single';
      const steps: Record<StepId, StepConfig> = normalizeStepsForContainer(v3.steps ?? buildSteps(), loadedContainerMode);
      // Criteria live in TWO places, written by two different surfaces:
      // this engine persists them to mode_config.v3Config.criteria, while the
      // Session Detail surface finalizes them into
      // prioritization_sessions.criteria_config (+ criteria_finalized_at).
      // This read path only ever looked at v3Config, so a session sitting at
      // CRITERIA_FINALIZED with two weighted criteria hydrated as zero — and
      // every S-step gate then reported "No criteria or values set", including
      // the S5 gate that keeps "Compute from values" disabled.
      // Fall back to criteria_config when v3Config carries none. Guarded on
      // empty, so a session that does have v3 criteria is untouched.
      let criteria: CriterionDef[] = v3.criteria ?? [];
      if (criteria.length === 0) {
        const cfg = session.criteriaConfig;
        const ids = cfg?.criteriaIds ?? [];
        if (ids.length > 0) {
          const fallbackWeight = 1 / ids.length;
          criteria = ids.map((id) => ({
            id,
            name: id.charAt(0).toUpperCase() + id.slice(1),
            weight: cfg?.weights?.[id] ?? fallbackWeight,
          }));
        }
      }
      // While the track's criteria are locked the room scores with the track's
      // weights (migration 1048). The server holds both saved copies to them
      // (criteria_config.weights and v3Config.criteria); this is the copy the S5
      // ranking computes from, so it follows too, even if this browser held an
      // older copy of the session. A session that has run keeps what it ran on.
      if (session.trackId && criteria.length > 0 && session.state !== 'RUN_COMPLETE' && session.state !== 'PUBLISHED') {
        try {
          const { readTrackLocks } = await import('../../hooks/useTrackStepLocks');
          const read = await readTrackLocks(session.trackId);
          if (read?.locks.criteriaLockedAt && read.trackWeights) {
            const { criteriaOnTrackWeights } = await import('../../lib/sessionCriteriaFromTrack');
            criteria = criteriaOnTrackWeights(criteria, read.trackWeights);
          }
        } catch {
          // The saved copy already follows the track; this only refreshes it.
        }
      }
      const values: Record<string, Record<string, number>> = v3.values ?? {};
      const scenarios: Scenario[] = (v3.scenarios ?? []).map((scenario: Scenario) => ({
        ...scenario,
        selectedItemIds: scenario.selectedItemIds ?? [],
      }));

      // Rebuild item list from session item IDs — resolve titles from store first,
      // then fall back to a direct DB fetch for anything not yet in memory.
      const items: EngineItem[] = [];
      const storeItems = useItemsStore.getState().items;
      const unresolvedIds: string[] = [];
      for (const itemId of session.itemIds ?? []) {
        const found = storeItems.find((i) => i.id === itemId);
        if (found) {
          items.push({ id: itemId, title: found.title, source: 'platform' });
        } else {
          items.push({ id: itemId, title: itemId, source: 'platform' }); // placeholder
          unresolvedIds.push(itemId);
        }
      }
      // Fetch titles for any IDs not in the store yet
      if (unresolvedIds.length > 0) {
        try {
          const { getSafeSupabaseClient } = await import('../../lib/supabaseSync');
          const supabase = getSafeSupabaseClient();
          if (supabase) {
            const { data: dbItems } = await supabase
              .from('items')
              .select('id, title')
              .in('id', unresolvedIds);
            if (dbItems) {
              const titleMap = Object.fromEntries(
                (dbItems as { id: string; title: string }[]).map((i) => [i.id, i.title])
              );
              for (const item of items) {
                if (titleMap[item.id]) item.title = titleMap[item.id];
              }
            }
          }
        } catch (_) {
          // title resolution is best-effort; UUIDs remain as fallback
        }
      }
      // R4: once the session is open for ranking, each item reads as it did then, not as later edited.
      if (session.criteriaFinalizedAt) {
        try {
          const { fetchItemWordingAsOf } = await import('../../lib/itemVersionsService');
          const then = await fetchItemWordingAsOf(items.map((i) => i.id), session.criteriaFinalizedAt);
          for (const item of items) {
            const w = then.get(item.id);
            if (w) item.title = w.title;
          }
        } catch (_) {
          // best-effort: today's titles remain
        }
      }
      // Also pull in any v3Config inline items (non-platform items added directly in the designer)
      const v3Items: Array<{ id: string; title: string; source?: string }> =
        (session.modeConfig as Record<string, unknown>)?.v3Config
          ? ((session.modeConfig as { v3Config?: { items?: Array<{ id: string; title: string; source?: string }> } }).v3Config?.items ?? [])
          : [];
      for (const v3Item of v3Items) {
        if (!items.find((i) => i.id === v3Item.id)) {
          items.push({ id: v3Item.id, title: v3Item.title, source: (v3Item.source as EngineItem['source']) ?? 'type' });
        }
      }
      for (const label of session.isolatedItems ?? []) {
        items.push({ id: uid('item'), title: label, source: 'type' });
      }

      // Load participants — resolve display names from public.users
      await useSessionParticipantsStore.getState().fetchParticipantsForSession(sessionId);
      const dbParticipants = useSessionParticipantsStore.getState().getParticipantsForSession(sessionId);

      // Resolve names from the users table (session_participants only stores userId)
      const participantUserIds = dbParticipants.map((p) => p.userId).filter(Boolean);
      let nameMap: Record<string, string> = {};
      if (participantUserIds.length > 0) {
        try {
          const { getSafeSupabaseClient } = await import('../../lib/supabaseSync');
          const supabase = getSafeSupabaseClient();
          if (supabase) {
            const { data: userRows } = await supabase
              .from('users')
              .select('id, name')
              .in('id', participantUserIds);
            if (userRows) {
              nameMap = Object.fromEntries(
                userRows.map((u: { id: string; name: string }) => [u.id, u.name])
              );
            }
          }
        } catch (_) {
          // name resolution is best-effort; fall back to userId slice
        }
      }

      const participants: ParticipantRef[] = dbParticipants.map((p) => ({
        id: p.id,
        name: nameMap[p.userId] ?? p.userId,
        userId: p.userId,
        role: (p.role as ParticipantRole) ?? 'contributor',
      }));

      // Ranking from ranking_output
      const ranking: string[] = (session.rankingOutput?.rankedItemIds as string[] | undefined) ?? [];

      set({
        event: {
          id: session.id,
          title: session.name,
          jobId: v3.jobId as string | undefined,
          primaryGoalStep: v3.primaryGoalStep as StepId | undefined,
          containerMode: loadedContainerMode,
          contextMode: (session.sessionContextMode as ContextMode) ?? 'app_linked',
          cycleName: v3.cycleName,
          trackName: v3.trackName,
          cycleId: session.cycleId ?? undefined,
          trackId: session.trackId ?? undefined,
          situationVector: session.situationVector ?? null,
          poolState: (v3.poolState as SessionEvent['poolState']) ?? 'open',
          driverWeightLink: v3.driverWeightLink as SessionEvent['driverWeightLink'],
          steps,
          participants,
          items,
          criteria,
          values,
          ranking,
          scenarios,
          activeScenarioId: v3.activeScenarioId,
          childSessions: v3.childSessions ?? {},
          stepHistory: v3.stepHistory ?? [],
        },
      });
      try { localStorage.setItem('v3ActiveEventId', sessionId); } catch { /* noop */ }
    } catch (err) {
      console.error('[SessionEngineV3] loadEvent failed:', err);
    }
  },

  resetEvent: () => {
    try { localStorage.removeItem('v3ActiveEventId'); } catch { /* noop */ }
    set({ event: null });
  },

  // ── Container / pool ──────────────────────────────────────────────────────
  setContainerMode: (mode) => patch(set, (e) => ({
    ...e,
    containerMode: mode,
    steps: normalizeStepsForContainer(e.steps, mode),
  })),
  setPoolState: (poolState) => patch(set, (e) => ({ ...e, poolState })),

  // ── Steps ─────────────────────────────────────────────────────────────────
  setStepMode: (step, mode) =>
    patch(set, (e) => ({
      ...e,
      steps: {
        ...e.steps,
        [step]: {
          ...e.steps[step],
          mode,
          runMode: e.containerMode === 'single' && mode === 'collaborative' ? 'live' : e.steps[step].runMode,
        },
      },
    })),
  setStepTool: (step, toolId) =>
    patch(set, (e) => ({ ...e, steps: { ...e.steps, [step]: { ...e.steps[step], toolId } } })),
  setStepGoal: (step, goal) =>
    patch(set, (e) => ({ ...e, steps: { ...e.steps, [step]: { ...e.steps[step], goal } } })),
  setStepRunMode: (step, runMode) =>
    patch(set, (e) => ({
      ...e,
      steps: { ...e.steps, [step]: { ...e.steps[step], runMode: e.containerMode === 'single' ? 'live' : runMode } },
    })),
  setStepRunStatus: (step, runStatus) =>
    patch(set, (e) => ({
      ...e,
      steps: { ...e.steps, [step]: { ...e.steps[step], runStatus } },
      stepHistory: [
        ...(e.stepHistory ?? []),
        { step, action: runStatus === 'complete' ? 'run' as const : 'revised' as const, timestamp: new Date().toISOString() },
      ],
    })),
  startStepRun: async (step) => {
    const event = get().event;
    if (!event || !event.id || event.id.startsWith('evt_')) return null;
    const cfg = event.steps[step];
    if (!cfg || cfg.mode !== 'collaborative') return null;

    const { useUserStore } = await import('../../stores/prioritization/userStore');
    const facilitatorId = useUserStore.getState().userId;
    if (!facilitatorId) return null;

    const { inviteParticipantsToStepRun } = await import('./inviteStepRun');

    if (event.containerMode === 'single') {
      get().setStepRunStatus(step, 'running');
      // Keep session.item_ids aligned with the Event Designer pool before go-live.
      try {
        const { useSessionStore } = await import('../../stores/prioritization/sessionStore');
        const fresh = get().event ?? event;
        const platformIds = fresh.items
          .filter((i) => i.source === 'platform' && !i.id.startsWith('item_'))
          .map((i) => i.id);
        useSessionStore.getState().setSessionItemIds(event.id, platformIds);
        useSessionStore.getState().setIsolatedItems(
          event.id,
          fresh.items.filter((i) => i.source === 'type').map((i) => i.title),
        );
        if (fresh.steps[step]?.toolId) {
          useSessionStore.getState().updateSession(event.id, {
            selectedToolId: fresh.steps[step].toolId,
          } as never);
        }
      } catch (e) {
        console.warn('[SessionEngineV3] item_ids sync before go-live failed:', e);
      }
      try {
        await inviteParticipantsToStepRun({
          event: get().event ?? event,
          step,
          sessionId: event.id,
          facilitatorId,
        });
      } catch (e) {
        console.warn('[SessionEngineV3] invite after single startStepRun failed:', e);
      }
      return event.id;
    }

    const existingChildId = event.childSessions[step];
    if (existingChildId) {
      get().setStepRunStatus(step, 'running');
      try {
        await inviteParticipantsToStepRun({
          event: get().event ?? event,
          step,
          sessionId: existingChildId,
          facilitatorId,
        });
      } catch (e) {
        console.warn('[SessionEngineV3] invite after reopen child failed:', e);
      }
      return existingChildId;
    }

    try {
      const { useSessionParticipantsStore } = await import('../../stores/prioritization/sessionParticipantsStore');
      const userId = facilitatorId;

      const platformItemIds = event.items
        .filter((item) => item.source === 'platform' && !item.id.startsWith('item_'))
        .map((item) => item.id);
      const isolatedItems = event.items
        .filter((item) => item.source !== 'platform' || item.id.startsWith('item_'))
        .map((item) => item.title);
      const childSteps = normalizeStepsForContainer(
        {
          ...event.steps,
          [step]: { ...event.steps[step], runStatus: 'running', runMode: cfg.runMode },
        },
        event.containerMode,
      );

      // Inherit track from the Catchball loop when the Event Designer never
      // stamped trackId (otherwise results cannot attach / desk cannot list).
      let childTrackId = event.trackId ?? null;
      if (!childTrackId && event.cycleId) {
        try {
          const { resolveLoopIdForSession } = await import('../../lib/catchballSessionLink');
          const { useCatchballLoopStore } = await import('../../stores/prioritization/catchballLoopStore');
          const loopId = resolveLoopIdForSession({
            sessionId: event.id,
            trackId: event.trackId,
            cycleId: event.cycleId,
          });
          const loop = loopId
            ? useCatchballLoopStore.getState().getLoop(loopId)
            : useCatchballLoopStore.getState().loops.find((l) => l.cycleId === event.cycleId);
          childTrackId = loop?.trackId ?? null;
        } catch {
          /* keep null */
        }
      }

      const childResult = await createSessionWithFeedback({
        name: `${event.title} — ${step} ${STEP_META[step].label}`,
        facilitatorId: userId,
        createdBy: userId,
        intent: intentFromGoalStep(step),
        sessionContextMode: event.contextMode,
        cycleId: event.cycleId,
        trackId: childTrackId,
        originModule: 'prioritization',
        itemIds: platformItemIds,
        isolatedItems,
        selectedToolId: cfg.toolId,
        modeConfig: ({
          timingMode: cfg.runMode === 'live' ? 'synchronous' : 'async',
          entryMode: 'facilitator',
          // The step's "Open (async input)" is canon's open; its run mode and
          // timing agree by the one rule (deriveRunMode, migration 1045).
          runMode: cfg.runMode === 'live' ? 'facilitator_live' : 'open',
          v3Config: {
            parentEventId: event.id,
            parentEventTitle: event.title,
            parentJobId: event.jobId,
            parentPrimaryGoalStep: event.primaryGoalStep,
            parentStep: step,
            steps: childSteps,
            criteria: event.criteria,
            values: event.values,
            scenarios: event.scenarios,
            activeScenarioId: event.activeScenarioId,
            containerMode: event.containerMode,
            contextMode: event.contextMode,
            primaryGoalStep: step,
            poolState: event.poolState,
            cycleName: event.cycleName,
            trackName: event.trackName,
            participants: event.participants.map(p => ({ userId: p.userId, name: p.name })),
            items: event.items.map(i => ({ id: i.id, title: i.title, source: i.source })),
          },
        } as unknown) as import('../../types/prioritization/session').SessionModeConfig,
      });
      const child = childResult.session;

      if (!child) {
        // startStepRun's own caller (ToolFraming) already reads lastCreateError
        // for its inline toast, but not every caller does (SessionEngineV3App's
        // `launch` silently no-ops on a null return) — dispatch too so the real
        // reason reaches the user either way.
        reportSessionCreateFailure('startStepRun', childResult.error ?? 'Session could not be created.');
        return null;
      }

      const addParticipant = useSessionParticipantsStore.getState().addParticipant;
      const { toSessionParticipantRole } = await import('./participantRoleMap');
      for (const participant of event.participants) {
        if (!participant.userId || participant.userId === userId) continue;
        await addParticipant({
          sessionId: child.id,
          userId: participant.userId,
          role: toSessionParticipantRole(participant.role),
          addedBy: userId,
        });
      }

      // If the parent Event is already on a Catchball loop, append this child sitting too.
      try {
        const { resolveLoopIdForSession, linkSessionToCatchballLoop } = await import('../../lib/catchballSessionLink');
        const parentLoopId = resolveLoopIdForSession({ sessionId: event.id });
        if (parentLoopId) {
          await linkSessionToCatchballLoop(parentLoopId, child.id);
        }
      } catch (linkErr) {
        console.warn('[SessionEngineV3] Child session created but Catchball link failed:', linkErr);
      }

      // Record parentage durably BEFORE the optimistic patch: if this write
      // cannot land, the child exists but nothing ties it to this Event, and a
      // silent orphan is worse than a visible failure.
      const nextChildren = { ...(get().event?.childSessions ?? {}), [step]: child.id };
      await persistChildLink(event.id, nextChildren);

      patch(set, (e) => ({
        ...e,
        childSessions: { ...e.childSessions, [step]: child.id },
        steps: { ...e.steps, [step]: { ...e.steps[step], runStatus: 'running' } },
        stepHistory: [
          ...(e.stepHistory ?? []),
          { step, action: 'run' as const, timestamp: new Date().toISOString(), note: `Child session ${child.id}` },
        ],
      }));

      try {
        await inviteParticipantsToStepRun({
          event: get().event ?? event,
          step,
          sessionId: child.id,
          facilitatorId,
        });
      } catch (e) {
        console.warn('[SessionEngineV3] invite after child startStepRun failed:', e);
      }

      return child.id;
    } catch (err) {
      console.warn('[SessionEngineV3] startStepRun failed:', err);
      return null;
    }
  },
  unlockStep: (step, reason) =>
    patch(set, (e) => ({
      ...e,
      steps: { ...e.steps, [step]: { ...e.steps[step], runStatus: 'not_started' } },
      stepHistory: [
        ...(e.stepHistory ?? []),
        { step, action: 'unlocked' as const, timestamp: new Date().toISOString(), note: reason },
      ],
    })),

  // ── Participants (S1) ─────────────────────────────────────────────────────
  addParticipant: (name, role, userId) => {
    const newParticipant: ParticipantRef = { id: uid('p'), name, userId, role };
    patch(set, (e) => ({ ...e, participants: [...e.participants, newParticipant] }));

    // Persist to session_participants for real workspace users
    if (userId) {
      void (async () => {
        try {
          const sessionId = get().event?.id;
          if (!sessionId || sessionId.startsWith('evt_')) return; // not yet persisted
          const { useSessionParticipantsStore } = await import('../../stores/prioritization/sessionParticipantsStore');
          const { useUserStore } = await import('../../stores/prioritization/userStore');
          const { toSessionParticipantRole } = await import('./participantRoleMap');
          await useSessionParticipantsStore.getState().addParticipant({
            sessionId,
            userId,
            role: toSessionParticipantRole(role),
            addedBy: useUserStore.getState().userId,
          });
        } catch (e) {
          console.warn('[SessionEngineV3] addParticipant DB sync failed:', e);
        }
      })();
    }
  },

  setParticipantRole: (id, role) => {
    const target = get().event?.participants.find((p) => p.id === id);
    if (!target || target.role === role) return;
    // In place: same id, same position in the roster. The old remove-then-add
    // fired two racing writes and could delete the participant after
    // re-adding them, so a chosen decider did not reliably persist (F7).
    patch(set, (e) => ({
      ...e,
      participants: e.participants.map((p) => (p.id === id ? { ...p, role } : p)),
    }));

    if (target.userId) {
      void (async () => {
        try {
          const sessionId = get().event?.id;
          if (!sessionId || sessionId.startsWith('evt_')) return; // not yet persisted
          const { useSessionParticipantsStore } = await import('../../stores/prioritization/sessionParticipantsStore');
          const { toSessionParticipantRole } = await import('./participantRoleMap');
          await useSessionParticipantsStore.getState().setParticipantRole({
            sessionId,
            userId: target.userId,
            role: toSessionParticipantRole(role),
          });
        } catch (e) {
          console.warn('[SessionEngineV3] setParticipantRole DB sync failed:', e);
        }
      })();
    }
  },

  removeParticipant: (id) => {
    const removed = get().event?.participants.find((p) => p.id === id);
    patch(set, (e) => ({ ...e, participants: e.participants.filter((p) => p.id !== id) }));

    // Remove from session_participants if they had a real userId
    if (removed?.userId) {
      void (async () => {
        try {
          const sessionId = get().event?.id;
          if (!sessionId || sessionId.startsWith('evt_')) return;
          const { useSessionParticipantsStore } = await import('../../stores/prioritization/sessionParticipantsStore');
          const pStore = useSessionParticipantsStore.getState();
          const rows = pStore.getParticipantsForSession(sessionId);
          const row = rows.find((r) => r.userId === removed.userId);
          if (row) await pStore.removeParticipant(row.id);
        } catch (e) {
          console.warn('[SessionEngineV3] removeParticipant DB sync failed:', e);
        }
      })();
    }
  },

  // ── Items (S2) ────────────────────────────────────────────────────────────
  addItems: (rows) => {
    const event = get().event;
    if (!event) return { added: [], conflicts: [] };
    const currentItems = event.items;
    const existingTitles = new Set(currentItems.map((i) => i.title.toLowerCase()));
    const existingIds = currentItems.map((i) => i.id);
    const relationshipState = useItemRelationshipsStore.getState();
    const candidateIds = rows.map((r) => r.id).filter((id): id is string => Boolean(id));
    const exclusivity = deriveExclusivityGroups(
      relationshipState.groups,
      relationshipState.groupMemberships,
      [...existingIds, ...candidateIds],
    );
    const next: EngineItem[] = [];
    const conflicts: AddItemsConflict[] = [];
    const acceptedIds = [...existingIds];
    for (const r of rows) {
      const title = r.title.trim();
      if (!title || existingTitles.has(title.toLowerCase())) continue;
      if (r.id) {
        const conflictingItemIds = cannotCoexistConflicts(exclusivity, acceptedIds, r.id);
        if (conflictingItemIds.length > 0) {
          conflicts.push({ candidate: { id: r.id, title }, conflictingItemIds });
          continue;
        }
      }
      existingTitles.add(title.toLowerCase());
      const item = { id: r.id ?? uid('item'), title, source: r.source };
      next.push(item);
      acceptedIds.push(item.id);
    }
    if (next.length === 0) return { added: [], conflicts };

    patch(set, (e) => ({ ...e, items: [...e.items, ...next] }), false);

    // Sync to DB — use addItems for platform items, setIsolatedItems for typed items
    void (async () => {
      try {
        const sessionId = get().event?.id;
        if (!sessionId || sessionId.startsWith('evt_')) return;
        const { useSessionStore } = await import('../../stores/prioritization/sessionStore');
        const store = useSessionStore.getState();

        // Full replace — delta addItems can leave item_ids incomplete when batches
        // race or when earlier adds failed silently.
        const allItems = get().event?.items ?? [];
        const platformIds = allItems
          .filter((i) => i.source === 'platform' && !i.id.startsWith('item_'))
          .map((i) => i.id);
        store.setSessionItemIds(sessionId, platformIds);

        const typedTitles = allItems.filter((i) => i.source === 'type').map((i) => i.title);
        store.setIsolatedItems(sessionId, typedTitles);
      } catch (e) {
        console.warn('[SessionEngineV3] addItems DB sync failed:', e);
      }
    })();

    // Sync V3 config with updated items
    const updated = get().event;
    if (updated?.id && !updated.persisting) syncV3Config(updated.id, updated);
    return { added: next, conflicts };
  },

  removeItem: (id) => {
    patch(set, (e) => {
      const values = { ...e.values };
      delete values[id];
      return { ...e, items: e.items.filter((i) => i.id !== id), values, ranking: e.ranking.filter((r) => r !== id) };
    });

    // Sync updated item list to DB
    void (async () => {
      try {
        const event = get().event;
        if (!event?.id || event.id.startsWith('evt_')) return;
        const { useSessionStore } = await import('../../stores/prioritization/sessionStore');
        const store = useSessionStore.getState();
        const platformIds = event.items.filter((i) => i.source === 'platform' && !i.id.startsWith('item_')).map((i) => i.id);
        store.setSessionItemIds(event.id, platformIds);
        const typedTitles = event.items.filter((i) => i.source === 'type').map((i) => i.title);
        store.setIsolatedItems(event.id, typedTitles);
      } catch (e) {
        console.warn('[SessionEngineV3] removeItem DB sync failed:', e);
      }
    })();
  },

  // ── Criteria (S3) ─────────────────────────────────────────────────────────
  setDriverWeightLink: (link) => {
    patch(set, (e) => ({ ...e, driverWeightLink: link }));
  },

  setCriteria: (criteria) => {
    patch(set, (e) => ({ ...e, criteria }));
    // patch() syncs the v3 config, and that sync is what mirrors criteria onto
    // the canonical session row (see syncV3Config).
  },

  setValue: (itemId, criterionId, value) =>
    patch(set, (e) => ({
      ...e,
      values: { ...e.values, [itemId]: { ...(e.values[itemId] ?? {}), [criterionId]: value } },
    })),

  /**
   * Fill S4 from values the workspace has already accepted.
   *
   * accepted_values is where a value lands once a workspace agrees on it, and
   * for a system-pulled criterion the criterion id IS the attribute key — so
   * these grids line up exactly. Nothing in this engine read that table, so a
   * workspace with every value already accepted still opened S4 at "0 / 42
   * values entered" and asked a facilitator to retype what the organisation had
   * already decided.
   *
   * Never overwrites: a value entered in this event wins over the accepted one,
   * because the event is where the newer conversation is happening. One patch
   * for the whole grid, not one per cell — per-cell writes each fire their own
   * fire-and-forget config sync and race each other.
   */
  hydrateValues: (incoming) =>
    patch(set, (e) => {
      const values = { ...e.values };
      let added = 0;
      for (const [itemId, byCriterion] of Object.entries(incoming)) {
        for (const [criterionId, value] of Object.entries(byCriterion)) {
          if (typeof values[itemId]?.[criterionId] === 'number') continue;
          values[itemId] = { ...(values[itemId] ?? {}), [criterionId]: value };
          added++;
        }
      }
      return added > 0 ? { ...e, values } : e;
    }),

  // ── Ranking (S5) ──────────────────────────────────────────────────────────
  runRanking: () => {
    const e = get().event;
    if (!e) return;
    patch(set, (cur) => ({ ...cur, ranking: computeRanking(cur) }), false);

    // Persist ranking output to DB
    void (async () => {
      try {
        const event = get().event;
        if (!event?.id || event.id.startsWith('evt_') || event.ranking.length === 0) return;
        const { useSessionStore, runSessionViaRpc } = await import('../../stores/prioritization/sessionStore');
        const store = useSessionStore.getState();

        const rankingOutput = {
          rankedItemIds: event.ranking,
          scores: {},
          normalizedScores: {},
          tiers: {},
          toolId: event.steps.S5?.toolId ?? 'v3_weighted_sum',
          toolName: event.steps.S5?.toolId ?? 'V3 Weighted Sum',
          // The settings this session was set up with — recorded with the
          // result, under the engine's keys, instead of an empty object.
          toolConfig: normalizeToolConfig(store.getSession(event.id)?.toolConfig),
          generatedAt: new Date(),
        };
        store.setRankingOutput(event.id, rankingOutput);

        // Producing a ranking is what "run complete" means, and until now
        // nothing said so: v3 wrote the results but left the session row at
        // DRAFT. Everything downstream reads that state, so the whole
        // governance chain was unreachable from this surface — Lift to Proposal
        // disables any session that is not RUN_COMPLETE or PUBLISHED, so the
        // ranking could never become a proposal, the Proposals lane stayed
        // empty, and Ratification succeeded while reporting "no priority lists
        // were in proposed status". The demo's closing act signed nothing.
        const current = store.getSession(event.id);
        const alreadyDone = current?.state === 'RUN_COMPLETE' || current?.state === 'PUBLISHED';
        if (!alreadyDone) {
          // Mirror the frame onto the canonical session row first. The
          // DRAFT → RUN_COMPLETE guard reads criteriaConfig.criteriaIds and a
          // resolvable tool id from the session, and v3 keeps both in its own
          // store — so the transition was refused for a session that plainly had
          // criteria and a computed ranking. S3's criterion ids ARE the workspace
          // attribute keys, so they carry over directly; the tool is named after
          // what "Compute from values" actually does.
          // The RUN_COMPLETE guard runs a tool-compatibility check whose
          // value-sufficiency test reads valueProposalStore.acceptedValues from
          // memory. Nothing in the v3 flow ever loads that store, so a workspace
          // with every value accepted still failed with "Tool incompatible:
          // Values Not Sufficient" — the values existed in the database and the
          // grid above showed 42/42, but the gate was looking at an empty store.
          try {
            const { useValueProposalStore } = await import('../../stores/prioritization/valueProposalStore');
            if (useValueProposalStore.getState().acceptedValues.length === 0) {
              await useValueProposalStore.getState().fetchFromSupabase();
            }
          } catch {
            // Non-fatal: the transition below reports its own reason if this
            // leaves the gate unsatisfied.
          }

          // The same shape of bug, one gate along. Before refusing DRAFT →
          // RUN_COMPLETE the guard asks whether the Track has accepted
          // criteria, and it asks the attribute-governance store in memory.
          // Nothing in this flow loads it, so a Track whose criteria are
          // accepted in the database still read as none and the session was
          // refused with "Criteria must be finalized before running (or use a
          // track with locked criteria)" — a sentence that is false about the
          // Track it names.
          if (event.trackId) {
            try {
              const { useAttributeGovernanceStore } = await import('../../stores/prioritization/attributeGovernanceStore');
              const governance = useAttributeGovernanceStore.getState();
              if (governance.getTrackConfigs(event.trackId).length === 0) {
                await governance.fetchTrackConfigs(event.trackId);
              }
            } catch {
              // Non-fatal for the same reason.
            }
          }

          const criterionIds = event.criteria.map((c) => c.id);
          // Weights too, not just the ids. criteriaConfig.weights is where
          // every session-level surface reads a criterion's share from, and v3
          // kept its weights only in its own mode_config blob — so a session
          // whose row plainly said Impact 50 / Effort 50 rendered both as 0%.
          const criterionWeights = Object.fromEntries(
            event.criteria.map((c) => [c.id, c.weight]),
          );
          const toolId = event.steps.S5?.toolId ?? current?.selectedToolId ?? 'simple_weighted_scoring';
          await store.updateSession(event.id, {
            selectedToolId: toolId,
            criteriaConfig: {
              ...(current?.criteriaConfig ?? {}),
              criteriaIds: criterionIds,
              weights: criterionWeights,
            },
          } as never);

          // One act, one write path (771). run_session writes the session row's
          // ranking_output, the RUN_COMPLETE state and the durable
          // session_results rows in ONE transaction. Before this, v3 wrote the
          // result rows itself, PATCHed ranking_output separately, and then
          // asked for the state change — three writes that could each fail on
          // their own, and a v3 ranking could survive a reload in one of them
          // but not the others. Non-UUID v3/demo item ids still live only in
          // ranking_output; the RPC keeps that rule.
          const { useUserStore } = await import('../../stores/prioritization/userStore');
          const actorId = useUserStore.getState().userId ?? undefined;
          const res = await runSessionViaRpc(event.id, rankingOutput, actorId);
          if (!res.success) {
            console.warn('[SessionEngineV3] could not mark session run-complete:', res.error);
          }
        }
      } catch (e) {
        console.warn('[SessionEngineV3] runRanking DB sync failed:', e);
      }
    })();

    // Sync V3 config with updated ranking
    const updated = get().event;
    if (updated?.id && !updated.persisting) syncV3Config(updated.id, updated);
  },

  setManualRanking: (ranking) => patch(set, (e) => ({ ...e, ranking })),

  // ── Scenarios ─────────────────────────────────────────────────────────────
  acceptCutLineToPool: (rankedItemIds, cutLine, notes) => {
    const event = get().event;
    if (!event) return { ok: false, error: 'No active event' };
    if (rankedItemIds.length === 0) return { ok: false, error: 'No items to cut' };

    const cut = Math.max(0, Math.min(cutLine, rankedItemIds.length));
    const selectedIds = rankedItemIds.slice(0, cut);
    if (selectedIds.length === 0) {
      return { ok: false, error: 'Cut line is at zero — move it up to keep at least one item' };
    }

    // Evaluate against the post-cut pool — items below the line are dropped,
    // so a cannot-coexist pair split by the cut is fine.
    const rel = useItemRelationshipsStore.getState();
    const exclusivity = deriveExclusivityGroups(rel.groups, rel.groupMemberships, selectedIds);
    const findings = evaluateExclusivity(exclusivity, selectedIds, selectedIds);
    if (findings.blocks.length > 0) {
      return {
        ok: false,
        error: 'Cannot accept cut: cannot-coexist items would remain above the line. Move one below the cut or remove it first.',
      };
    }

    const applied = applyCutLineToPoolItems({
      items: event.items,
      values: event.values,
      ranking: event.ranking,
      rankedItemIds,
      cutLine: cut,
    });

    const scenario: Scenario = {
      id: uid('sc'),
      name: `Accepted cut at ${cut}`,
      cutLine: cut,
      selectedItemIds: applied.keptIds,
      notes: notes?.trim() || 'Accepted as item pool (S2)',
    };

    const s2 = event.steps.S2;
    const nextSteps = {
      ...event.steps,
      S2: {
        ...s2,
        // Declared or collaborative — accepting the cut completes S2 selection.
        runStatus: s2.mode === 'collaborative' ? ('complete' as const) : s2.runStatus,
      },
    };

    patch(set, (e) => ({
      ...e,
      items: applied.items,
      values: applied.values,
      ranking: applied.ranking,
      poolState: 'locked',
      steps: nextSteps,
      scenarios: [...e.scenarios, scenario],
      activeScenarioId: scenario.id,
      stepHistory: [
        ...(e.stepHistory ?? []),
        {
          step: 'S2' as const,
          action: 'run' as const,
          timestamp: new Date().toISOString(),
          note: `Accepted cut line: kept ${applied.keptIds.length}, dropped ${applied.droppedIds.length}`,
        },
      ],
    }));

    void (async () => {
      try {
        const updated = get().event;
        if (!updated?.id || updated.id.startsWith('evt_')) return;
        const { useSessionStore } = await import('../../stores/prioritization/sessionStore');
        const store = useSessionStore.getState();
        const platformIds = updated.items
          .filter((i) => i.source === 'platform' && !i.id.startsWith('item_'))
          .map((i) => i.id);
        store.setSessionItemIds(updated.id, platformIds);
        const typedTitles = updated.items.filter((i) => i.source === 'type').map((i) => i.title);
        store.setIsolatedItems(updated.id, typedTitles);
        syncV3Config(updated.id, updated);
      } catch (e) {
        console.warn('[SessionEngineV3] acceptCutLineToPool DB sync failed:', e);
      }
    })();

    return {
      ok: true,
      keptCount: applied.keptIds.length,
      droppedCount: applied.droppedIds.length,
    };
  },

  addScenario: (name, cutLine, selectedItemIds = [], notes) =>
    patch(set, (e) => {
      const sc: Scenario = { id: uid('sc'), name, cutLine, selectedItemIds, notes };
      return { ...e, scenarios: [...e.scenarios, sc], activeScenarioId: sc.id };
    }),
  updateScenario: (id, p) =>
    patch(set, (e) => ({ ...e, scenarios: e.scenarios.map((s) => (s.id === id ? { ...s, ...p } : s)) })),
  removeScenario: (id) =>
    patch(set, (e) => ({
      ...e,
      scenarios: e.scenarios.filter((s) => s.id !== id),
      activeScenarioId: e.activeScenarioId === id ? undefined : e.activeScenarioId,
    })),
  setActiveScenario: (id) => patch(set, (e) => ({ ...e, activeScenarioId: id })),
}));
